/**
 * @file SessionsPage.tsx
 * @brief Super Admin → Sessions page. Cross-platform browse, filter,
 *        and inspect interpretation sessions.
 *
 * @details
 * - URL query params drive filter state so reloads / shareable links
 *   preserve the view.
 * - Mutations invalidate the list query → table refreshes automatically.
 * - Clicking a row opens the session page (`SessionDetailPage`, platform
 *   scope): metadata only, no utterances, audio, comments or transcript
 *   (B2/W4, DES 22-10-2026).
 *
 * @dependencies
 * - features/sessions/api (TanStack Query hooks)
 * - features/sessions/components/* (filters, Sheet detail, badges)
 * - components/data/DataTable
 *
 * @references
 * - BACKEND/src/features/admin/routes.js (sessions endpoints)
 *
 * @author DES
 * @date 26-05-2026
 * @updated 29-07-2026 — GridBlock end-to-end; session detail as Sheet
 */

import { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Building2 } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import {
  DataTable,
  type PaginationState,
} from "@/components/data/DataTable";
import type { TableRowGroup } from "@/components/data/groupRowsBy";
import { companyGroupMeta } from "@/shared/utils/tableGroups";
import {
  GridBand,
  GridBlock,
  GridCell,
} from "@/features/analytics/components/GridBlock";
import { gridPageBleedClass } from "@/features/analytics/components/gridPageBleed";
import { PageDock, pageWithDockClass } from "@/components/ui/PageDock";
import { Avatar } from "@/components/ui/Avatar";
import { DateRangePicker } from "@/components/ui/DateRangePicker";
import {
  formatDateTime,
  formatDurationSeconds,
  formatFullName,
  formatLanguagePair,
  formatMoneyMinor,
  getInitials,
} from "@/shared/utils/formatters";
import {
  sessionsCompanyGroupKey,
  sessionsOwnerGroupKey,
  useSessionsGroupRows,
  useSessionsGroupSummary,
  useSessionsList,
} from "./api";
import type { AdminSession, AdminSessionsListParams } from "./schemas";
import {
  SessionsFilters,
  SessionsHeaderFilters,
} from "./components/SessionsFilters";
import { SessionStatusBadge } from "./components/SessionStatusBadge";
import { SessionKindBadge } from "./components/SessionKindBadge";
import { SessionRowActions } from "./components/SessionRowActions";
import { StaffNoContentNote } from "@/components/feedback/StaffNoContent";

// ----------------------------------------------------------------------
// URL <-> filter-state helpers
// ----------------------------------------------------------------------
function paramsFromSearch(search: URLSearchParams): AdminSessionsListParams {
  const status = search.get("status");
  const kind = search.get("kind");
  return {
    page: Number(search.get("page")) || 1,
    limit: Number(search.get("limit")) || 25,
    search: search.get("q") || undefined,
    status:
      status === "in_progress" ||
      status === "completed" ||
      status === "failed" ||
      status === "cancelled" ||
      status === "deleted"
        ? status
        : undefined,
    kind: kind === "standard" || kind === "realtime" ? kind : undefined,
    user_id: search.get("user_id") || undefined,
    company_id: search.get("company_id") || undefined,
    from: search.get("from") || undefined,
    to: search.get("to") || undefined,
    source_language: search.get("source_language") || undefined,
    target_language: search.get("target_language") || undefined,
  };
}

function searchFromParams(p: AdminSessionsListParams): URLSearchParams {
  const usp = new URLSearchParams();
  if (p.page && p.page !== 1) usp.set("page", String(p.page));
  if (p.limit && p.limit !== 25) usp.set("limit", String(p.limit));
  if (p.search) usp.set("q", p.search);
  if (p.status) usp.set("status", p.status);
  if (p.kind) usp.set("kind", p.kind);
  if (p.user_id) usp.set("user_id", p.user_id);
  if (p.company_id) usp.set("company_id", p.company_id);
  if (p.from) usp.set("from", p.from);
  if (p.to) usp.set("to", p.to);
  if (p.source_language) usp.set("source_language", p.source_language);
  if (p.target_language) usp.set("target_language", p.target_language);
  return usp;
}

// ----------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------
export function SessionsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => paramsFromSearch(searchParams), [searchParams]);

  // A session opens as its own page (01-10-2026).
  const navigate = useNavigate();
  const openSession = (id: string) => navigate(`/admin/sessions/${encodeURIComponent(id)}`);

  const isGrouped = !params.company_id && !params.user_id;
  /**
   * Grouped (no company or user picked): every company -> user pair from the
   * server with its session count, not just the sessions loaded so far (DES
   * 01-10-2026: "haan superadmin pages pe bhi yehi kar do"), as on company
   * Invoices. Only a user (leaf) group fetches its sessions when opened, 25
   * at a time. No stat cards here, so the flat list is off while grouped.
   */
  const list = useSessionsList(params, { enabled: !isGrouped });
  const summary = useSessionsGroupSummary(params, isGrouped);
  // Open groups (company and user keys) live in the URL (`open=key:batches,...`).
  const openParam = searchParams.get("open") ?? "";
  const openGroups = useMemo(() => {
    const map = new Map<string, number>();
    for (const part of openParam.split(",")) {
      const [key, n] = part.split(":");
      if (key) map.set(key, Math.max(1, Number(n) || 1));
    }
    return map;
  }, [openParam]);
  const setOpenGroups = (update: (prev: Map<string, number>) => Map<string, number>) => {
    const next = update(openGroups);
    const usp = new URLSearchParams(searchParams);
    const value = [...next].map(([k, n]) => (n > 1 ? `${k}:${n}` : k)).join(",");
    if (value) usp.set("open", value);
    else usp.delete("open");
    setSearchParams(usp, { replace: true });
  };
  // A search opens every group, so matches are visible.
  const searching = isGrouped && Boolean(params.search?.trim());
  // Only leaves (`company/owner`) fetch rows; company keys just expand.
  const openList = useMemo(() => {
    const keys = searching
      ? (summary.data ?? []).map((g) => sessionsOwnerGroupKey(g.company_id, g.owner_id))
      : [...openGroups.keys()].filter((key) => key.includes("/"));
    return keys.map((key) => ({ key, batches: openGroups.get(key) ?? 1 }));
  }, [searching, summary.data, openGroups]);
  const groupRows = useSessionsGroupRows(params, openList);
  const groups = useMemo<TableRowGroup<AdminSession>[] | undefined>(() => {
    if (!isGrouped || !summary.data?.length) return undefined;
    const companies = new Map<string, TableRowGroup<AdminSession>>();
    for (const g of summary.data) {
      const companyKey = sessionsCompanyGroupKey(g.company_id);
      let company = companies.get(companyKey);
      if (!company) {
        company = {
          key: companyKey,
          ...companyGroupMeta(g.company_name),
          rows: [],
          children: [],
          total: 0,
        };
        companies.set(companyKey, company);
      }
      const key = sessionsOwnerGroupKey(g.company_id, g.owner_id);
      const idx = openList.findIndex((o) => o.key === key);
      const q = idx >= 0 ? groupRows[idx] : undefined;
      const name = formatFullName(g.first_name, g.last_name);
      company.children!.push({
        key,
        label: name !== "—" ? name : g.email ?? "—",
        avatarSrc: g.avatar_url,
        avatarGender: g.gender,
        avatarInitials: getInitials(g.first_name, g.last_name, g.email),
        rows: q?.data?.sessions ?? [],
        total: g.count,
        loading: Boolean(q?.isFetching),
        error: Boolean(q?.isError),
        onRetry: () => void q?.refetch(),
      });
      company.total = (company.total ?? 0) + g.count;
    }
    const byLabel = (a: { label: string }, b: { label: string }) =>
      a.label.localeCompare(b.label);
    return [...companies.values()]
      .map((c) => ({ ...c, children: [...(c.children ?? [])].sort(byLabel) }))
      .sort(byLabel);
  }, [isGrouped, summary.data, openList, groupRows]);
  const rows = isGrouped
    ? (groups ?? []).flatMap((c) => (c.children ?? []).flatMap((u) => u.rows))
    : list.data?.sessions ?? [];
  const pagination: PaginationState | undefined =
    !isGrouped && list.data?.pagination
      ? {
          page: list.data.pagination.page,
          limit: list.data.pagination.limit,
          total: list.data.pagination.total,
          totalPages: list.data.pagination.totalPages,
        }
      : undefined;

  const setParams = (next: AdminSessionsListParams) =>
    setSearchParams(searchFromParams(next), { replace: false });

  const columns = useMemo<ColumnDef<AdminSession>[]>(
    () => [
      {
        id: "session",
        header: "Session",
        cell: ({ row }) => {
          const s = row.original;
          if (isGrouped) {
            return (
              <p className="truncate font-mono text-xs text-foreground">
                {s.session_id}
              </p>
            );
          }
          return (
            <div className="flex items-center gap-3">
              <Avatar
                src={s.person_a_avatar_url}
                gender={s.person_a_gender}
                initials={getInitials(
                  s.owner_first_name,
                  s.owner_last_name,
                  s.owner_email,
                )}
                alt=""
                size="md"
                tone="brand"
              />
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">
                  {formatFullName(s.owner_first_name, s.owner_last_name) !== "—"
                    ? formatFullName(s.owner_first_name, s.owner_last_name)
                    : (s.owner_email ?? "—")}
                </p>
                <p className="truncate font-mono text-xs text-muted-foreground">
                  {s.session_id}
                </p>
              </div>
            </div>
          );
        },
      },
      ...(isGrouped
        ? []
        : [
            {
              id: "company",
              header: "Company",
              cell: ({ row }) =>
                row.original.company_name ? (
                  <span className="inline-flex items-center gap-1.5 text-sm">
                    <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="truncate">{row.original.company_name}</span>
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground">Personal</span>
                ),
            } satisfies ColumnDef<AdminSession>,
          ]),
      {
        id: "kind",
        header: "Kind",
        cell: ({ row }) => <SessionKindBadge kind={row.original.session_kind} />,
      },
      {
        id: "status",
        header: "Status",
        cell: ({ row }) => <SessionStatusBadge status={row.original.status} />,
      },
      {
        id: "languages",
        header: "Languages",
        cell: ({ row }) => (
          <span className="text-sm tabular-nums">
            {formatLanguagePair(
              row.original.source_language,
              row.original.target_language,
            )}
          </span>
        ),
      },
      {
        id: "duration",
        header: "Duration",
        cell: ({ row }) => (
          <span className="text-sm tabular-nums">
            {formatDurationSeconds(row.original.duration_seconds)}
          </span>
        ),
      },
      {
        id: "cost",
        header: "Cost",
        cell: ({ row }) => (
          <span className="text-sm font-medium tabular-nums">
            {formatMoneyMinor(
              row.original.total_cents,
              row.original.currency ?? "GBP",
            )}
          </span>
        ),
      },
      {
        id: "started",
        header: "Started",
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {formatDateTime(row.original.started_at)}
          </span>
        ),
      },
      /* Row actions — mobile long-press parity (View / Delete audio /
         Delete artifacts / Delete all). Sits at the very end of the
         row so the "…" trigger is always reachable regardless of
         column ordering. */
      {
        id: "actions",
        header: () => <span className="sr-only">Actions</span>,
        cell: ({ row }) => (
          <div className="flex items-center justify-end">
            <SessionRowActions
              sessionId={row.original.session_id}
              onView={(id) => openSession(id)}
            />
          </div>
        ),
      },
    ],
    [isGrouped],
  );

  return (
    <div className={gridPageBleedClass(`pb-6 ${pageWithDockClass}`)}>
      <GridBlock>
        <GridBand className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1 basis-72">
            <h2 className="text-xl font-semibold tracking-tight">Sessions</h2>
            <p className="mt-1 max-w-xl text-sm text-muted-foreground">
              Browse, filter, and inspect interpretation sessions across the
              whole platform. Click a row for its owner, languages, minutes
              and cost.
            </p>
            <StaffNoContentNote className="mt-2" />
          </div>
          {/* The filters, top right (DES 01-10-2026): text pills, then the
              date circle. */}
          <div className="flex flex-wrap items-center justify-end gap-2.5">
            <SessionsHeaderFilters value={params} onChange={setParams} />
            <DateRangePicker
              variant="circle"
              from={params.from}
              to={params.to}
              onChange={({ from, to }) => setParams({ ...params, from, to, page: 1 })}
            />
          </div>
        </GridBand>

        <GridCell padded={false} className="border-b-0">
          <DataTable<AdminSession, unknown>
            variant="cell"
            columns={columns}
            data={rows}
            groups={groups}
            expandAllGroups={searching}
            expandedGroupKeys={isGrouped ? [...openGroups.keys()] : undefined}
            onGroupExpandChange={(key, expanded) =>
              setOpenGroups((prev) => {
                const next = new Map(prev);
                if (expanded) next.set(key, next.get(key) ?? 1);
                else next.delete(key);
                return next;
              })
            }
            onGroupLoadMore={(key) =>
              setOpenGroups((prev) => new Map(prev).set(key, (prev.get(key) ?? 1) + 1))
            }
            groupNestedStyle={isGrouped ? "timeline" : "plain"}
            groupItemLabel="sessions"
            isLoading={isGrouped ? summary.isLoading : list.isLoading}
            isFetching={isGrouped ? summary.isFetching : list.isFetching}
            error={isGrouped ? summary.error : list.error}
            onRetry={() => (isGrouped ? summary.refetch() : list.refetch())}
            rowKey={(s) => s.session_id}
            pagination={pagination}
            onPageChange={(page) => setParams({ ...params, page })}
            onLimitChange={(limit) =>
              setParams({ ...params, limit, page: 1 })
            }
            onRowClick={(s) => openSession(s.session_id)}
            emptyShape="session"
            emptyTitle="No sessions match those filters"
            emptyDescription="Adjust the filters or wait for fresh sessions to roll in."
          />
        </GridCell>
      </GridBlock>

      {/* The search, docked at the bottom like Notes (DES 01-10-2026). */}
      <PageDock>
        <SessionsFilters value={params} onChange={setParams} />
      </PageDock>

    </div>
  );
}
